import { logger } from '../../../../utils/common/logger'
import { DOMUtils } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { IdentityEvent } from '../../../common/events/identity'
import { IdentityScreen } from '../../enums'
import { PasswordRequirementsList } from '../../modules/password-requirements-list'
import { ShowPasswordButton } from '../../modules/show-password-button'
import { ILRApiClientConfig, LoginService } from '../loginradius/services/login-service'
import { ISuccessResponse } from '../loginradius/widgets/login-widget'
import { IErrorType } from './base'
import { IdentityFormAPI } from './form-api'
import { IdentityFormUtils } from './form-utils'

export class ResetPasswordForm {
    private static DOM = {
        class: {
            passwordFieldContainer: 'form-container__field--password'
        },
        form: 'resetPasswordForm',
        id: {
            confirmPasswordInputField: 'id_confirm_password',
            passwordInputField: 'id_password',
            resetPasswordContainer: 'id_reset_password_form',
        }
    }
    private submissionEventName = 'reset-password'
    private lrApiConfig: ILRApiClientConfig

    /**
     * Listens for the form validated event in the Reset Password form
     */
    public async init(lrApiConfig?: ILRApiClientConfig) {
        logger('debug', 'Initializing Reset Password form')
        this.lrApiConfig = lrApiConfig
        const form = this.getFormElement()

        if (form) {
            // add show/hide password button
            await ShowPasswordButton.initDOM(`#${ResetPasswordForm.DOM.id.resetPasswordContainer}`)

            document.addEventListener(IdentityEvent.FORM_VALIDATED, async (event: CustomEvent) => {
                const formType = event.detail.form

                if (
                    formType === ResetPasswordForm.DOM.form &&
                    this.validateForm(`[data-id-form=${ResetPasswordForm.DOM.form}]`)
                ) {
                    await this.submitForm(form)
                }
            })
            await PasswordRequirementsList.initDOM(ResetPasswordForm.DOM.class.passwordFieldContainer)
            DOMUtils.addClass(document.body, 'identity-screen-form--active')
        }
        logger('debug', 'Reset Password form initialized')
    }

    /**
     * Calls Reset Password LR API endpoint
     */
    public async submitForm(form: HTMLFormElement) {
        const formData = new FormData(form)
        // The dataObj creation below might be simplified with the Object.fromEntries but our current
        // ts config doesn't recognize it.
        // const dataObj: any = Object.fromEntries(data)
        const dataObj: any = {}
        formData.forEach((value, key) => dataObj[key] = value)

        try {
            const response = await IdentityFormAPI.resetPassword(dataObj)
            // the response comes in the following format:
            // {
            //   'Data': {
            //     'Profile': {...},
            //     'access_token': '...',
            //     'expires_in': '..',
            //   },
            //   'IsPosted': true
            // }
            const responseData = response.Data
            return this.onSuccess(responseData)
        } catch (error) {
            const errors = IdentityFormUtils.structureErrors(error)
            return this.onError(errors)
        }
    }

    /**
     * Success callback function
     */
    protected async onSuccess(response: ISuccessResponse) {
        LoginService.sendFormSubmissionEvent(this.submissionEventName)
        // hide form and show success message
        DOMUtils.removeClass(document.body, IdentityScreen.FORM)
        DOMUtils.addClass(document.body, IdentityScreen.SUCCESS)

        const accessToken = response.access_token
        if (accessToken) {
            // store access token in local storage
            localStorage.setItem(Store.LR_TOKEN_KEY, accessToken)
            if (!response.jwttoken && this.lrApiConfig) {
                // we need jwt token so that analytics dispatched by
                // the handleLoginSuccess can determine whether the user is a subscriber
                const lrApiClient = LoginService.getLRApiClient(this.lrApiConfig, accessToken)
                response.jwttoken = await lrApiClient.getJwtToken()
            }
            const DELAY = 1500
            setTimeout(async () => {
                await this.authenticate(response)}, DELAY
            )
        }
    }

    /**
     * Error callback function
     * @param errors any
     *
     */
    protected onError(errors: any) {
        logger('error', errors)
        LoginService.sendSubmissionErrorAnalytics(errors, this.submissionEventName)
        IdentityFormUtils.clearErrors()
        const error = (Array.isArray(errors)) ? errors[0] : undefined

        if (error && (error.key === 'fatal_errors')) {
            // Invalid and expired token page
            DOMUtils.removeClass(document.body, IdentityScreen.FORM)
            DOMUtils.addClass(document.body, IdentityScreen.ERROR)
        } else {
            LoginService.showErrors(errors)
            IdentityFormUtils.showErrors(`[data-id-form=${ResetPasswordForm.DOM.form}]`, errors)
        }

    }
    protected async authenticate(response: ISuccessResponse) {
       const accessToken = response.access_token
       if (accessToken && sessionStorage && localStorage) {
           const urlParams = new URLSearchParams(window.location.search)
           LoginService.setSessionStorage(urlParams)
           await LoginService.handleLoginSuccess(response)
       } else {
           logger('error', 'Password-reset authentication not possible')
       }
    }

    /**
     * Get HTMLFormElement representation of the Email Verification form
     */
    private getFormElement(): HTMLFormElement | undefined {
        return document.querySelector(`[data-id-form=${ResetPasswordForm.DOM.form}]`)
    }

    /**
     * Validate password and confirm password inputs
     */
    private passwordConfirmation(passwordValue: string, confirmPasswordValue: string): boolean {
        return passwordValue === confirmPasswordValue
    }

    /**
     * Validate form
     */
    private validateForm(formSelector: string) {
        const form = document.querySelector(formSelector)
        const passwordElement: HTMLInputElement = form.querySelector(
            `#${ResetPasswordForm.DOM.id.passwordInputField}`
        )
        const confirmPasswordElement: HTMLInputElement = form.querySelector(
            `#${ResetPasswordForm.DOM.id.confirmPasswordInputField}`
        )
        const errors: IErrorType[] = []
        const msg = 'Passwords must have a minimum of 10 characters, contain upper and lowercase letters, 1 number ' +
            'and 1 special character.'

        if (!PasswordRequirementsList.validateRequirements(passwordElement.value).every(Boolean)) {
            errors.push({key: passwordElement.name, message: msg})
        }
        if (!PasswordRequirementsList.validateRequirements(confirmPasswordElement.value).every(Boolean)) {
            errors.push({key: confirmPasswordElement.name, message: msg})
        }

        if (!errors.length && !this.passwordConfirmation(passwordElement.value, confirmPasswordElement.value)) {
            errors.push(
                {
                    key: confirmPasswordElement.name,
                    message: 'The Confirm Password field does not match the Password field.'
                }
            )
        }
        IdentityFormUtils.clearErrors()
        if (errors.length > 0) {
            IdentityFormUtils.showErrors(formSelector, errors)
        }

        return !errors.length
    }
}
